import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import {
    ApiErrorLogListItem,
    ApiErrorLogEntry,
    ApiErrorLogSearchRequest,
    ApiErrorLogSearchResult
} from 'src/app/services/api-error-log.service';
import { EPortalApiLogService } from 'src/app/services/eportal-api-log.service';

@Component({
    selector: 'app-eportal-api-log',
    templateUrl: './eportal-api-log.component.html',
    styleUrls: ['./eportal-api-log.component.scss']
})
export class EportalApiLogComponent implements OnInit {
    // Search filters
    fromDate: string = '';
    toDate: string = '';
    selectedLevel: string = '';
    selectedDomain: string = '';
    searchText: string = '';

    // Pagination
    pageNumber = 1;
    pageSize = 50;
    totalCount = 0;
    totalPages = 0;

    // Data
    items: ApiErrorLogListItem[] = [];
    loading = false;

    // Detail view
    selectedEntry: ApiErrorLogEntry | null = null;
    loadingDetail = false;
    expandedRow: number | null = null;
    generatedPrompt: string | null = null;

    levels = ['Warning', 'Error', 'Fatal'];

    constructor(
        private ePortalApiLogService: EPortalApiLogService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        // Default: last 7 days
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        this.fromDate = this.toIsoDateString(weekAgo);
        this.toDate = this.toIsoDateString(now);

        // Read deep-link query params from integration-monitor
        const params = this.route.snapshot.queryParamMap;
        const domain = params.get('domain');
        const search = params.get('search');
        const level = params.get('level');
        if (domain) this.selectedDomain = domain;
        if (search) this.searchText = search;
        if (level) this.selectedLevel = level;

        this.loadData();
    }

    loadData(): void {
        this.loading = true;
        this.expandedRow = null;
        this.selectedEntry = null;

        const request: ApiErrorLogSearchRequest = {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize
        };

        if (this.fromDate) {
            request.fromDate = new Date(this.fromDate).toISOString();
        }
        if (this.toDate) {
            const to = new Date(this.toDate);
            to.setHours(23, 59, 59, 999);
            request.toDate = to.toISOString();
        }
        if (this.selectedLevel) {
            request.level = this.selectedLevel;
        }
        if (this.searchText?.trim()) {
            request.searchText = this.searchText.trim();
        }
        if (this.selectedDomain?.trim()) {
            request.tenantDomain = this.selectedDomain.trim();
        }

        this.ePortalApiLogService.search(request).subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    this.items = response.result.items;
                    this.totalCount = response.result.totalCount;
                    this.pageNumber = response.result.pageNumber;
                    this.totalPages = response.result.totalPages;
                } else {
                    this.items = [];
                    this.totalCount = 0;
                }
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.toastService.show({
                    type: 'error',
                    data: [this.translateService.instant('EPortalApiLog.loadError')]
                });
            }
        });
    }

    toggleDetail(item: ApiErrorLogListItem): void {
        if (this.expandedRow === item.id) {
            this.expandedRow = null;
            this.selectedEntry = null;
            return;
        }

        this.expandedRow = item.id;
        this.loadingDetail = true;
        this.selectedEntry = null;

        this.ePortalApiLogService.getById(item.id).subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    this.selectedEntry = response.result;
                }
                this.loadingDetail = false;
            },
            error: () => {
                this.loadingDetail = false;
            }
        });
    }

    onSearch(): void {
        this.pageNumber = 1;
        this.loadData();
    }

    resetFilters(): void {
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        this.fromDate = this.toIsoDateString(weekAgo);
        this.toDate = this.toIsoDateString(now);
        this.selectedLevel = '';
        this.selectedDomain = '';
        this.searchText = '';
        this.pageNumber = 1;
        this.loadData();
    }

    onPageChange(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.pageNumber = page;
        this.loadData();
    }

    onPageSizeChange(size: number): void {
        this.pageSize = size;
        this.pageNumber = 1;
        this.loadData();
    }

    getLevelBadgeClass(level?: string): string {
        switch (level?.toLowerCase()) {
            case 'fatal': return 'badge-soft-dark';
            case 'error': return 'badge-soft-danger';
            case 'warning': return 'badge-soft-warning';
            case 'information': return 'badge-soft-info';
            case 'debug': return 'badge-soft-secondary';
            default: return 'badge-soft-secondary';
        }
    }

    formatTimestamp(ts: string): string {
        if (!ts) return '-';
        const d = new Date(ts);
        return d.toLocaleString('nb-NO', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    }

    formatPropertiesXml(xml?: string): string {
        if (!xml) return '';
        try {
            return xml
                .replace(/></g, '>\n<')
                .replace(/\n\s*\n/g, '\n');
        } catch {
            return xml;
        }
    }

    formatLogEventJson(json?: string): string {
        if (!json) return '';
        try {
            return JSON.stringify(JSON.parse(json), null, 2);
        } catch {
            return json;
        }
    }

    goBack(): void {
        this.router.navigate(['/system-admin/integration-monitor']);
    }

    copyDebugPrompt(entry: ApiErrorLogEntry): void {
        const prompt = this.buildDebugPrompt(entry);
        navigator.clipboard.writeText(prompt).then(() => {
            this.generatedPrompt = prompt;
            this.toastService.show({
                type: 'success',
                data: [this.translateService.instant('EPortalApiLog.promptCopiedToast')]
            });
        });
    }

    private buildDebugPrompt(entry: ApiErrorLogEntry): string {
        const parts: string[] = [];

        parts.push('# Feilsøking av ePortal-feil\n');
        parts.push('Du er en senior .NET-utvikler som feilsøker en produksjonsfeil i ePortal (ASP.NET 8 + Angular + SQL Server).\n');

        parts.push('## Feildetaljer');
        parts.push(`- **Tidspunkt:** ${this.formatTimestamp(entry.timeStamp)}`);
        parts.push(`- **Nivå:** ${entry.level || 'Ukjent'}`);
        if (entry.tenantDomain) parts.push(`- **Klient:** ${entry.tenantDomain}`);
        if (entry.tenantUser) parts.push(`- **Bruker:** ${entry.tenantUser}`);
        parts.push('');

        parts.push('## Feilmelding');
        parts.push('```');
        parts.push(entry.message || '(ingen melding)');
        parts.push('```\n');

        if (entry.exception) {
            parts.push('## Exception og stack trace');
            parts.push('```csharp');
            parts.push(entry.exception);
            parts.push('```\n');
        }

        if (entry.properties) {
            const props = this.extractProperties(entry.properties);
            if (props.length > 0) {
                parts.push('## Kontekst (fra logg-properties)');
                for (const [key, value] of props) {
                    parts.push(`- **${key}:** ${value}`);
                }
                parts.push('');
            }
        }

        parts.push('## Oppgave');
        parts.push('1. Analyser feilmeldingen og stack trace.');
        parts.push('2. Identifiser rotårsaken.');
        parts.push('3. Foreslå en konkret fix med kodeeksempel.');
        parts.push('4. Forklar hva som gikk galt og hvorfor.');
        if (entry.exception) {
            parts.push('5. Sjekk om det er en kjent felle i ePortal (RepoDb [Name]-bug, JSON-serialisering, nullable mismatch, etc.).');
        }

        return parts.join('\n');
    }

    private extractProperties(xml: string): [string, string][] {
        const result: [string, string][] = [];
        const regex = /<property\s+key="([^"]+)">([^<]*)<\/property>/gi;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(xml)) !== null) {
            result.push([match[1], match[2]]);
        }
        return result;
    }

    private toIsoDateString(date: Date): string {
        return date.toISOString().split('T')[0];
    }
}
